import {
  mapGlobalCustomRangeToSectionFilters,
  mapGlobalPeriodToSectionFilters,
} from '../hooks/dashboardPeriod'

const pad = (value) => String(value).padStart(2, '0')

export const toYmd = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate())

/**
 * Inclusive calendar window for a dashboard global period, in the viewer's local zone.
 * 7 days matches the dashboard preset (today and the six days before it).
 */
export function rangeForDashboardPeriod(period, { start = '', end = '' } = {}, now = new Date()) {
  const today = startOfDay(now)
  if (period === 'today') {
    const ymd = toYmd(today)
    return { datePreset: 'today', dateFrom: ymd, dateTo: ymd }
  }
  if (period === '7d') {
    const from = new Date(today)
    from.setDate(from.getDate() - 6)
    return { datePreset: '7d', dateFrom: toYmd(from), dateTo: toYmd(today) }
  }
  if (period === 'month') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1)
    return { datePreset: 'custom', dateFrom: toYmd(from), dateTo: toYmd(today) }
  }
  if (period === 'quarter') {
    const from = new Date(today)
    from.setDate(from.getDate() - 89)
    return { datePreset: 'quarter', dateFrom: toYmd(from), dateTo: toYmd(today) }
  }
  if (period === 'custom') {
    return {
      datePreset: 'custom',
      dateFrom: String(start || '').slice(0, 10),
      dateTo: String(end || '').slice(0, 10),
    }
  }
  const from = new Date(today)
  from.setDate(from.getDate() - 6)
  return { datePreset: '7d', dateFrom: toYmd(from), dateTo: toYmd(today) }
}

/**
 * Period the undelivered KPI is actually using.
 * A custom global period that has not been applied yet stays on the last applied window.
 */
export function effectiveUndeliveredPeriod(filters = {}) {
  const period = filters.globalPeriod || filters.salesRange || '7d'
  if (period === 'custom') {
    const start = filters.globalStart || filters.salesStart || ''
    const end = filters.globalEnd || filters.salesEnd || ''
    if (start && end) return { period: 'custom', start, end }
    const fallback =
      filters.salesRange && filters.salesRange !== 'custom' ? filters.salesRange : '7d'
    return {
      period: fallback,
      start: filters.salesStart || '',
      end: filters.salesEnd || '',
    }
  }
  return { period, start: '', end: '' }
}

/** Query string for the undelivered-quantity detail page, scoped to the current global filters. */
export function buildUndeliveredQuantitySearch(filters = {}, now = new Date()) {
  const params = new URLSearchParams()
  if (filters.locationIds) params.set('locationIds', filters.locationIds)
  if (filters.categoryIds) params.set('categoryIds', filters.categoryIds)
  if (filters.organizationIds) params.set('organizationIds', filters.organizationIds)

  const effective = effectiveUndeliveredPeriod(filters)
  const range = rangeForDashboardPeriod(
    effective.period,
    { start: effective.start, end: effective.end },
    now,
  )
  if (
    effective.period === 'month' ||
    effective.period === 'quarter' ||
    effective.period === 'today' ||
    effective.period === '7d'
  ) {
    params.set('datePreset', effective.period)
  } else {
    params.set('datePreset', range.datePreset)
  }
  if (effective.period === 'custom' || range.datePreset === 'custom') {
    if (range.dateFrom) params.set('dateFrom', range.dateFrom)
    if (range.dateTo) params.set('dateTo', range.dateTo)
  }
  return params.toString()
}

/** Rebuild dashboard filters when returning from the detail page after a refresh. */
export function buildDashboardReturnSearch(
  {
    locationIds = '',
    categoryIds = '',
    organizationIds = '',
    datePreset = '7d',
    dateFrom = '',
    dateTo = '',
  } = {},
  now = new Date(),
) {
  const params = new URLSearchParams()
  if (locationIds) params.set('locationIds', locationIds)
  if (categoryIds) params.set('categoryIds', categoryIds)
  if (organizationIds) params.set('organizationIds', organizationIds)

  let mapped
  if (datePreset === 'today' || datePreset === '7d') {
    mapped = mapGlobalPeriodToSectionFilters(datePreset)
  } else if (datePreset === '3d') {
    const today = startOfDay(now)
    const from = new Date(today)
    from.setDate(from.getDate() - 2)
    mapped = mapGlobalCustomRangeToSectionFilters({
      start: toYmd(from),
      end: toYmd(today),
    })
  } else if (dateFrom && dateTo) {
    mapped = mapGlobalCustomRangeToSectionFilters({ start: dateFrom, end: dateTo })
  } else {
    mapped = mapGlobalPeriodToSectionFilters('7d')
  }
  Object.entries(mapped).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  return params.toString()
}
